import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate } from 'react-router-dom';
import { User, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function AuthGateway() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const emailVal = document.getElementById('email')?.value || email;
        const passwordVal = document.getElementById('password')?.value || password;

        if (!emailVal || !passwordVal) {
            setError('Debes ingresar usuario y contraseña');
            setIsLoading(false);
            return;
        }

        try {
            const result = await login(emailVal, passwordVal);

            if (result.success) {
                // Éxito: Guardar sesión (ya se hace en Context, pero reforzamos para cumplir blueprint)
                localStorage.setItem('auth_token', 'session_' + Date.now());
                localStorage.setItem('agent_user', JSON.stringify(result.user));

                // Redirección segura
                window.location.href = '/#/dashboard';
            } else {
                // Error controlado del server
                setError(result.error || 'Usuario o contraseña incorrectos');
            }
        } catch (err) {
            // Error de red o código
            setError('Error de conexión. Intenta nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="absolute inset-0 bg-[#0F172A] flex items-center justify-center p-4 z-50">
            <style>{`
                .password-field-container {
                    margin-bottom: 20px;
                }
                .form-label {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #e2e8f0;
                }
                .password-input-wrapper {
                    position: relative;
                }
                .password-input {
                    width: 100% !important;
                    padding: 12px 44px 12px 40px !important;
                    border-radius: 10px !important;
                    border: 1.5px solid rgba(148,163,184,.3) !important;
                    background: rgba(30,41,59,.6) !important;
                    color: #e2e8f0 !important;
                    font-size: 15px !important;
                    height: 48px !important;
                }
                .password-input:focus {
                    outline: none;
                    border-color: #3b82f6 !important;
                }
                .password-toggle-btn {
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 6px;
                    color: #94a3b8;
                    z-index: 20;
                }
                .password-toggle-btn:hover {
                    color: #3b82f6;
                }
                .eye-icon {
                    width: 20px;
                    height: 20px;
                    stroke: currentColor;
                    fill: none;
                    stroke-width: 2;
                }
                .login-error {
                    margin-top: 12px;
                    margin-bottom: 12px;
                    padding: 10px 14px;
                    background: rgba(239, 68, 68, 0.15);
                    border: 1px solid rgba(239, 68, 68, 0.4);
                    color: #fecaca;
                    border-radius: 8px;
                    font-size: 14px;
                    text-align: center;
                }
                .login-btn {
                    width: 100%;
                    height: 48px;
                    background: #2563eb;
                    color: white;
                    font-weight: 700;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.125rem;
                }
                .login-btn:hover {
                    background: #3b82f6;
                }
                .login-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>
            {/* Background Deco */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] pointer-events-none"></div>

            {/* Professional Card */}
            <Card className="w-full max-w-md bg-white/10 border-white/10 text-white backdrop-blur-2xl p-8 rounded-3xl shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500"></div>

                <div className="text-center mb-8 relative z-10">
                    <div className="w-16 h-16 bg-blue-600/30 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-blue-500/30">
                        <ShieldCheck className="w-8 h-8 text-blue-400" />
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight mb-2">Acceso de Agente</h2>
                    <p className="text-gray-400">Portal de Gestión Servicios Futura</p>
                </div>

                <div
                    id="loginError"
                    className="login-error"
                    style={{ display: error ? 'block' : 'none' }}
                >
                    {error}
                </div>

                <form onSubmit={handleLogin} className="space-y-5 relative z-10">
                    <div className="space-y-2">
                        <Label className="text-gray-300">Correo Profesional</Label>
                        <div className="relative">
                            <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="agente@serviciosfutura.com"
                                className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-gray-600 h-12 hover:border-blue-500/50 focus:border-blue-500 transition-colors"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className="password-field-container">
                        <div className="flex justify-between">
                            <Label className="form-label text-gray-300">Contraseña</Label>
                            <Link to="/forgot-password" class="text-xs text-blue-400 hover:text-blue-300">¿Olvidaste tu contraseña?</Link>
                        </div>
                        <div className="password-input-wrapper">
                            <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400 z-10" />
                            <Input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder="••••••••"
                                autoComplete="current-password"
                                className="password-input"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                id="togglePassword"
                                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg className="eye-icon" viewBox="0 0 24 24">
                                        <path d="M1 12s4-8 11-8 11 8 11 8" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                ) : (
                                    <svg className="eye-icon" viewBox="0 0 24 24">
                                        <path d="M1 12s4-8 11-8 11 8 11 8" />
                                        <path d="M12 15a3 3 0 1 1 0-6" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <button
                        id="loginButton"
                        type="submit"
                        disabled={isLoading}
                        className="login-btn shadow-lg shadow-blue-900/50 transition-all active:scale-[0.98]"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        ) : null}
                        {isLoading ? 'Verificando...' : <>Entrar al Panel <ArrowRight className="ml-2 w-5 h-5" /></>}
                    </button>
                </form>

                <div className="mt-8 text-center relative z-10">
                    <p className="text-gray-400 text-sm">
                        ¿Eres nuevo aquí? <Link to="/register" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">Solicitar acreditación</Link>
                    </p>
                </div>
            </Card>
        </div>
    );
}
